<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import enUS from 'vue-datepicker-next/locale/en.es';
import huHU from 'vue-datepicker-next/locale/hu.es';
import { required as requiredRule } from '@regle/rules';
import { useItpFormControl, useItpFormField } from './useItpForm';
import { useItpDatetime, type DatetimeType } from './useItpDatetime';

const props = defineProps<{
  modelValue?: string | Date | null;
  name: string;
  id?: string;

  type?: DatetimeType;
  placeholder?: string;

  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;

  clearable?: boolean;

  size?: 'sm' | 'md' | 'lg';

  format?: string;

  valueType?: string;

  locale?: 'en' | 'hu';
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | Date | null): void;
  (event: 'change', value: string | Date | null): void;
  (event: 'input', value: string | Date | null): void;
}>();

const { isDisabled, isReadonly, isReadonlyOrDisabled } =
  useItpFormControl(props);

const { touch } = useItpFormField(props, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

const effectiveType = computed(() => props.type || 'date');

const { langObj, displayFormat, modelValueType } = useItpDatetime({
  type: effectiveType.value,
  format: props.format,
  valueType: props.valueType,
  locale: props.locale,
});

const lang = computed(() => {
  return props.locale === 'hu' ? huHU : enUS;
});

const inputClass = computed(() => {
  const size = props.size || 'md';
  const sizeModifier = size === 'md' ? '' : ` form-control-${size}`;
  return `form-control${sizeModifier}`;
});

const internalValue = ref<string | Date | null | undefined>(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    internalValue.value = val;
  },
);

const modelProxy = computed({
  get: () => internalValue.value,
  set: (val: any) => {
    internalValue.value = val;
    emit('update:modelValue', val);
    emit('change', val);
    emit('input', val);
  },
});
</script>

<template>
  <DatePicker
    :id="id || name"
    v-model:value="modelProxy"
    :name="name"
    :type="effectiveType"
    :size="size"
    :placeholder="placeholder"
    :disabled="isReadonlyOrDisabled"
    :clearable="clearable"
    :format="displayFormat"
    :value-type="modelValueType"
    :lang="lang"
    :input-class="inputClass"
    :editable="true"
    @keydown.enter.prevent="touch"
    @close="touch"
  />
</template>
<style lang="scss">
.mx-datepicker .mx-input-wrapper input {
  padding-right: 2rem;
}
</style>
